/**
 * Trip Atlas controls shared by the portfolio atlas and each property's atlas:
 * period (all / year / quarter), colour mode, the legend, the money summary and
 * its breakdown, and the callout HUD. Every number traces to a trip row or a
 * Zoho deal; a cost Zoho does not have reads "not recorded", never $0.
 */
import { memo, useEffect, useState } from 'react';
import type { CalloutAnchor } from './TripAtlas3D';
import {
  COLOR_BANDS,
  COLOR_BY_LABEL,
  LONG_TRIP_DAYS,
  TRIP_STATES,
  type AtlasPeriod,
  type ColorBy,
  type TripAtlas,
  type TripState,
  type TripSummary,
} from '../../lib/tripAtlas';

export const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
export const pct = (n: number) => `${Math.round(n * 100)}%`;

const BG: Record<string, string> = {
  surface: 'bg-surface',
  'surface-3': 'bg-surface-3',
  'info-soft': 'bg-info-soft',
  info: 'bg-info',
  'success-soft': 'bg-success-soft',
  success: 'bg-success',
  'accent-gold': 'bg-accent-gold',
  'warning-soft': 'bg-warning-soft',
  warning: 'bg-warning',
};

const HATCH = 'repeating-linear-gradient(-49deg, var(--success) 0 1px, transparent 1px 4px)';

/** Legend swatch for a trip state: the axon drawing states, in semantic tokens. */
export function Swatch({ state }: { state: TripState }) {
  const s = TRIP_STATES[state];
  const border = s.dash ? 'border-dashed' : 'border-solid';
  const fill =
    state === 'settled' ? 'bg-success' : state === 'history' ? 'bg-success-soft' : state === 'review' ? 'bg-warning-soft' : state === 'cancelled' ? 'bg-transparent' : 'bg-surface';
  const edge = state === 'review' ? 'border-warning' : state === 'planned' || state === 'cancelled' ? 'border-text-muted' : 'border-text';
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-3 w-6 shrink-0 border ${border} ${fill} ${edge} ${state === 'cancelled' ? 'border-dotted' : ''}`}
      style={s.hatch ? { backgroundImage: HATCH } : undefined}
    />
  );
}

export function BandSwatch({ fill }: { fill: string }) {
  return <span aria-hidden="true" className={`inline-block h-3 w-6 shrink-0 border border-text ${BG[fill] ?? 'bg-surface'}`} />;
}

/** A long-trip tag, used in lists and panels. */
export function LongTag({ days }: { days: number }) {
  if (days < LONG_TRIP_DAYS) return null;
  return <span className="shrink-0 rounded bg-info px-1.5 py-px text-[10px] font-semibold text-text-inverse">{days} days</span>;
}

// ─── Segmented controls ──────────────────────────────────────────────────────

function Segmented<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded border border-border bg-surface p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={`min-h-[30px] rounded px-2.5 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-text ${value === o.value ? 'bg-text font-semibold text-text-inverse' : 'text-text-muted hover:bg-surface-strong'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * All trips · one year · one quarter. Picking Year or Quarter keeps the year
 * already chosen (or the latest), so switching granularity never loses place.
 */
export function PeriodBar({ period, years, onChange }: { period: AtlasPeriod; years: number[]; onChange: (p: AtlasPeriod) => void }) {
  const latest = years[years.length - 1] ?? new Date().getFullYear();
  const year = period.kind === 'all' ? latest : period.year;
  const quarter = period.kind === 'quarter' ? period.quarter : 1;
  const sel = 'min-h-[32px] rounded border border-border bg-surface px-2 text-xs text-text focus:outline-none focus:ring-2 focus:ring-text';
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Segmented
        label="Period"
        value={period.kind}
        options={[
          { value: 'all', label: 'All trips' },
          { value: 'year', label: 'Year' },
          { value: 'quarter', label: 'Quarter' },
        ]}
        onChange={(k) => onChange(k === 'all' ? { kind: 'all' } : k === 'year' ? { kind: 'year', year } : { kind: 'quarter', year, quarter })}
      />
      {period.kind !== 'all' && (
        <>
          <label className="sr-only" htmlFor="atlas-year">Year</label>
          <select
            id="atlas-year"
            className={sel}
            value={year}
            onChange={(e) => {
              const y = Number(e.target.value);
              onChange(period.kind === 'quarter' ? { kind: 'quarter', year: y, quarter } : { kind: 'year', year: y });
            }}
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </>
      )}
      {period.kind === 'quarter' && (
        <Segmented
          label="Quarter"
          value={`q${quarter}` as 'q1' | 'q2' | 'q3' | 'q4'}
          options={(['q1', 'q2', 'q3', 'q4'] as const).map((q) => ({ value: q, label: q.toUpperCase() }))}
          onChange={(q) => onChange({ kind: 'quarter', year, quarter: Number(q[1]) as 1 | 2 | 3 | 4 })}
        />
      )}
    </div>
  );
}

export function ColorByControl({ value, onChange }: { value: ColorBy; onChange: (c: ColorBy) => void }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-text-muted">Colour</span>
      <Segmented label="Colour by" value={value} options={(Object.keys(COLOR_BY_LABEL) as ColorBy[]).map((c) => ({ value: c, label: COLOR_BY_LABEL[c] }))} onChange={onChange} />
    </div>
  );
}

// ─── Legend for the colour mode ──────────────────────────────────────────────

export function ColorLegend({
  mode,
  rows,
  onToggle,
  isOn,
}: {
  mode: ColorBy;
  /** Band rows with visible counts (non-state modes) or state rows. */
  rows: { key: string; label: string; fill?: string; count: number; filterWord?: string }[];
  onToggle: (word: string) => void;
  isOn: (word: string) => boolean;
}) {
  return (
    <div className="space-y-1">
      <p className="font-semibold">
        {mode === 'state' ? 'Trip state' : COLOR_BY_LABEL[mode]}
        <span className="font-normal text-text-muted"> · visible trips</span>
      </p>
      {rows.map((r) => {
        const on = r.filterWord ? isOn(r.filterWord) : false;
        const swatch = mode === 'state' ? <Swatch state={r.key as TripState} /> : <BandSwatch fill={r.fill!} />;
        const body = (
          <>
            {swatch}
            <span className="flex-1">{r.label}</span>
            <span className="tabular-nums text-text-muted">{r.count}</span>
          </>
        );
        return r.filterWord ? (
          <button
            key={r.key}
            type="button"
            aria-pressed={on}
            disabled={r.count === 0 && !on}
            onClick={() => onToggle(r.filterWord!)}
            className={`-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-2 rounded px-1 py-0.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-text disabled:cursor-default disabled:opacity-50 ${on ? 'bg-surface-strong font-semibold' : 'enabled:hover:bg-surface-strong'}`}
          >
            {body}
          </button>
        ) : (
          <p key={r.key} className="flex items-center gap-2 py-0.5">{body}</p>
        );
      })}
      {mode !== 'state' && (
        <p className="flex items-center gap-2 pt-0.5 text-text-muted">
          <span aria-hidden="true" className="inline-block h-3 w-6 shrink-0 border border-text bg-surface" style={{ backgroundImage: 'repeating-linear-gradient(-49deg, var(--text-muted) 0 1px, transparent 1px 4px)' }} />
          Hatched: imported history, no Books invoice yet
        </p>
      )}
    </div>
  );
}

/** Legend rows for a band mode. */
export function bandRows(mode: Exclude<ColorBy, 'state'>, counts: { key: string; count: number }[]) {
  return COLOR_BANDS[mode].map((b) => ({
    ...b,
    count: counts.find((c) => c.key === b.key)?.count ?? 0,
    filterWord: mode === 'length' && b.key === 'long' ? 'long' : mode === 'revenue' && b.key === 'none' ? 'unpriced' : mode === 'margin' && b.key === 'unknown' ? 'nocost' : undefined,
  }));
}

// ─── Money ───────────────────────────────────────────────────────────────────

/** Profit, honestly: over the trips whose cost Zoho records, and how many those are. */
export function profitText(s: TripSummary): { value: string; note: string } {
  if (s.trips === 0) return { value: '—', note: 'No trips in view' };
  if (s.profit === null) return { value: 'Not recorded', note: `Labor and equipment cost are blank on the Zoho deals of all ${s.withDeal} trip${s.withDeal === 1 ? '' : 's'}` };
  const covered = s.costedTrips === s.pricedTrips ? 'all priced trips' : `${s.costedTrips} of ${s.pricedTrips} priced trips`;
  return { value: `${money(s.profit)}${s.margin === null ? '' : ` · ${pct(s.margin)}`}`, note: `Revenue minus Zoho labor + equipment cost, on ${covered}` };
}

export function SummaryCards({ s, compact = false }: { s: TripSummary; compact?: boolean }) {
  const p = profitText(s);
  const cards: { label: string; value: string; note?: string }[] = [
    { label: 'Trips', value: String(s.trips), note: s.longTrips ? `${s.longTrips} of ${LONG_TRIP_DAYS}+ days` : `none of ${LONG_TRIP_DAYS}+ days` },
    { label: 'Days on site', value: String(s.days) },
    {
      label: 'Revenue',
      value: money(s.revenue),
      note: s.trips - s.pricedTrips > 0 ? `${s.trips - s.pricedTrips} trip${s.trips - s.pricedTrips === 1 ? '' : 's'} with no deal amount` : 'Zoho deal amounts',
    },
    { label: 'Profit', value: p.value, note: p.note },
  ];
  return (
    <dl className={`grid gap-2 ${compact ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 xl:grid-cols-4'}`}>
      {cards.map((c) => (
        <div key={c.label} className="rounded border border-border bg-surface px-3 py-2">
          <dt className="text-[11px] text-text-muted">{c.label}</dt>
          <dd className={`font-semibold tabular-nums text-text ${compact ? 'text-base' : 'text-lg'}`}>{c.value}</dd>
          {c.note && <dd className="text-[11px] leading-snug text-text-muted">{c.note}</dd>}
        </div>
      ))}
    </dl>
  );
}

/**
 * Per-year or per-quarter bars: revenue length, trip and day counts, profit
 * where Zoho has costs. A row drills into its period.
 */
export function Breakdown({
  title,
  rows,
  activeKey,
  onPick,
}: {
  title: string;
  rows: { key: string; label: string; summary: TripSummary; period: AtlasPeriod }[];
  activeKey: string | null;
  onPick: (p: AtlasPeriod) => void;
}) {
  const max = Math.max(1, ...rows.map((r) => r.summary.revenue));
  return (
    <section aria-label={title} className="space-y-1">
      <h3 className="text-xs font-semibold text-text">{title}</h3>
      <ul className="space-y-1">
        {rows.map((r) => {
          const s = r.summary;
          const on = r.key === activeKey;
          return (
            <li key={r.key}>
              <button
                type="button"
                aria-pressed={on}
                onClick={() => onPick(r.period)}
                className={`grid w-full grid-cols-[52px_minmax(0,1fr)_auto] items-center gap-2 rounded px-1.5 py-1 text-left text-xs hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-text ${on ? 'bg-surface-strong font-semibold' : ''}`}
              >
                <span className="text-text">{r.label}</span>
                <span className="relative h-3 overflow-hidden rounded-sm bg-surface-3" aria-hidden="true">
                  <span className="absolute inset-y-0 left-0 bg-success" style={{ width: `${(s.revenue / max) * 100}%` }} />
                </span>
                <span className="whitespace-nowrap tabular-nums text-text-muted">
                  {s.trips} trip{s.trips === 1 ? '' : 's'} · {s.days}d{s.longTrips ? ` · ${s.longTrips} long` : ''} · <span className="text-text">{money(s.revenue)}</span>
                  {s.profit !== null ? ` · ${money(s.profit)} profit` : ''}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ─── Callout HUD ─────────────────────────────────────────────────────────────

export const MAX_CALLOUTS = 5;
const CALLOUT_W = 220;

export type AnchorSink = (anchors: CalloutAnchor[]) => void;

/**
 * Boxed labels on vertical leaders from each block's front-left edge, like the
 * axon drawings. It owns the anchor state, so a pan or zoom re-renders only
 * this layer — never the page or the result list.
 */
export const CalloutLayer = memo(function CalloutLayer({
  atlas,
  selected,
  onSelect,
  sinkRef,
  widthRef,
  byYear = false,
}: {
  atlas: TripAtlas;
  selected: string | null;
  onSelect: (id: string) => void;
  sinkRef: React.MutableRefObject<AnchorSink>;
  widthRef: React.RefObject<HTMLDivElement>;
  /** One property's atlas: the title is the date, not the property name. */
  byYear?: boolean;
}) {
  const [anchors, setAnchors] = useState<CalloutAnchor[]>([]);
  useEffect(() => {
    sinkRef.current = setAnchors;
    return () => {
      sinkRef.current = () => {};
    };
  }, [sinkRef]);
  const cw = widthRef.current?.clientWidth ?? 800;
  return (
    <div className="pointer-events-none absolute inset-0">
      {anchors
        .filter((a) => a.visible)
        .slice(0, cw < 640 ? 3 : MAX_CALLOUTS)
        .sort((a, b) => a.x - b.x)
        .map((a, i) => {
          // Keep the box inside the canvas; the leader still lands on its anchor.
          const left = Math.max(8, Math.min(a.x - 6, cw - CALLOUT_W - 8));
          const t = atlas.byId.get(a.tripId);
          const b = atlas.blockById.get(a.tripId);
          if (!t || !b) return null;
          const top = Math.min(Math.max(18, 56 + i * 52), a.y - 16);
          const isSel = a.tripId === selected;
          const rev = t.finance?.revenue;
          return (
            <div key={a.tripId}>
              <div className="absolute w-px bg-text" style={{ left: a.x, top: top + 34, height: Math.max(0, a.y - top - 34) }} />
              <div className="absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-text" style={{ left: a.x, top: a.y }} />
              <button
                type="button"
                onClick={() => onSelect(a.tripId)}
                className={`pointer-events-auto absolute border-text bg-surface px-2.5 pb-2 pt-1.5 text-left text-[11px] leading-tight focus:outline-none focus-visible:ring-2 focus-visible:ring-text ${isSel ? 'border-2' : 'border'}`}
                style={{ left, top, maxWidth: CALLOUT_W }}
              >
                <span className="flex items-center gap-1.5 font-semibold text-text">
                  <span className="truncate">{byYear ? t.start_date : t.location_name}</span>
                  <LongTag days={b.days} />
                </span>
                <span className="block text-text-muted">
                  <span className="font-mono">{t.project_number}</span>
                  {byYear ? '' : ` · ${t.start_date}`}
                  {b.days > 1 && b.days < LONG_TRIP_DAYS ? ` +${b.days - 1}d` : ''}
                  {rev != null ? ` · ${money(rev)}` : ''}
                </span>
              </button>
            </div>
          );
        })}
    </div>
  );
});
